import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AiTopicSettingsService } from '../ai-topic-settings.service';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-sub-topic-modal',
    templateUrl: './sub-topic-modal.component.html',
    styleUrls: ['./sub-topic-modal.component.css'],
    standalone: false
})
export class SubTopicModalComponent implements OnInit {
  subTopicForm: FormGroup;
  loading = false;
  topics: any[] = [];
  mode: 'add' | 'edit' = 'add';
  subTopic: any = null;

  constructor(
    private dialogRef: MatDialogRef<SubTopicModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private aiTopicService: AiTopicSettingsService,
    private messageService: MessageService
  ) {
    this.mode = data.mode || 'add';
    this.subTopic = data.subTopic || null;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadTopics();
  }

  private initializeForm(): void {
    this.subTopicForm = this.fb.group({
      topic_id: [null, [Validators.required]],
      sub_topic_name: ['', [Validators.required, Validators.minLength(2)]],
      secondary_prompt: ['', [Validators.required, Validators.minLength(5)]],
      status: [true]
    });

    // If editing, populate form with existing data
    if (this.mode === 'edit' && this.subTopic) {
      this.subTopicForm.patchValue({
        topic_id: this.subTopic.topic_id,
        sub_topic_name: this.subTopic.sub_topic_name,
        secondary_prompt: this.subTopic.secondary_prompt,
        status: this.subTopic.status
      });
    }
  }

  loadTopics(): void {
    this.loading = true;
    this.aiTopicService.getAllTopics(this.data.userId, this.data.siteId).subscribe({
      next: (response) => {
        console.log('Topics response:', response); // Debug log
        this.topics = response.data || response;
        console.log('Topics loaded:', this.topics); // Debug log
        
        // If editing, set the selected topic after topics are loaded
        if (this.mode === 'edit' && this.subTopic) {
          this.subTopicForm.patchValue({ topic_id: this.subTopic.topic_id });
        }
        
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading topics:', error);
        this.loading = false;
        this.showError('Error loading topics. Please try again.');
      }
    });
  }

  onSubmit(): void {
    if (this.subTopicForm.valid) {
      this.loading = true;
      const formData = this.subTopicForm.value;

      if (this.mode === 'edit') {
        // Update existing subtopic
        const subTopicData = {
          sub_topic_name: formData.sub_topic_name,
          secondary_prompt: formData.secondary_prompt,
          is_system: this.data.userId === 1,
          status: formData.status
        };

        this.aiTopicService.updateSubTopic(this.subTopic.sub_topic_id, subTopicData).subscribe({
          next: (response) => {
            this.loading = false;
            this.showSuccess('SubTopic updated successfully');
            this.dialogRef.close(true);
          },
          error: (error) => {
            console.error('Error updating subtopic:', error);
            this.loading = false;
            this.showError('Error updating subtopic. Please try again.');
          }
        });
      } else {
        // Add new subtopic
        const subTopicData = {
          site_id: this.data.userId === 1 ? null : this.data.siteId,
          topic_id: formData.topic_id,
          sub_topic_name: formData.sub_topic_name,
          secondary_prompt: formData.secondary_prompt,
          is_system: this.data.userId === 1
        };

        this.aiTopicService.addSubTopic(subTopicData).subscribe({
          next: (response) => {
            this.loading = false;
            this.showSuccess('SubTopic added successfully');
            this.dialogRef.close(true);
          },
          error: (error) => {
            console.error('Error adding subtopic:', error);
            this.loading = false;
            this.showError('Error adding subtopic. Please try again.');
          }
        });
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.subTopicForm.controls).forEach(key => {
      const control = this.subTopicForm.get(key);
      control?.markAsTouched();
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getFieldError(fieldName: string): string {
    const field = this.subTopicForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${fieldName.replace('_', ' ')} is required`;
      }
      if (field.errors['minlength']) {
        return `${fieldName.replace('_', ' ')} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }
}
